import trofeuBannerCompartilhamento from 'assets/img/2026/trofeu_banner_compartilhamento.png';
import { ModalEscolhaPreliminarEtapa3 } from 'components/ModalEscolhaPreliminarEtapa3';
import ModalWarning from 'components/ui/ModalWarning';
import { VotacaoEtapa3TutorialModal } from 'components/VotacaoEtapa3TutorialModal';
import { useAuth } from 'context/AuthContext';
import { useModal } from 'context/ModalContext/Modal.context';
import { usePeriodoFase } from 'hooks/useControlDate';
import { useVotacaoEtapa3 } from 'hooks/useVotacaoEtapa3';
import { toBlob } from 'html-to-image';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IoCloudDownloadOutline, IoPaperPlane, IoSearch, IoTimeOutline } from 'react-icons/io5';
import { useLocation } from 'react-router-dom';
import { buscarIniciativasEtapa3 } from 'services/iniciativa.service';
import { getTutorial } from 'services/tutorial.service';
import { getTutorialEtapa3IdForUser } from 'types/etapa3';
import { IniciativaFormatada } from 'types/iniciativa';
import { Tutorial } from 'types/tutorial';
import { PHASE_IDS } from 'utils/constants';
import { formatarDiaMes } from 'utils/cronograma';
import { exportarIniciativasPdf, VotacaoEtapa1ExportItem } from 'utils/votacao-etapa1-export';
import {
  CATEGORIAS_PARTICIPANTES,
  CategoriasParticipantes,
} from '../HomePublic/components/CategoriasParticipantes';
import { Header } from '../HomePublic/components/Header';
import { IniciativaCard } from '../HomePublic/components/IniciativaCard';
import './style.scss';

function useCountdown(target: Date | null) {
  const [timeLeft, setTimeLeft] = useState(() => (target ? Math.max(0, target.getTime() - Date.now()) : 0));

  useEffect(() => {
    if (!target) return;
    const interval = setInterval(() => {
      setTimeLeft(Math.max(0, target.getTime() - Date.now()));
    }, 1000);
    return () => clearInterval(interval);
  }, [target]);

  const totalSeconds = Math.floor(timeLeft / 1000);
  const days = Math.floor(totalSeconds / (3600 * 24));
  const hours = Math.floor((totalSeconds % (3600 * 24)) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const fmt = (n: number) => String(n).padStart(2, '0');

  if (days > 0) {
    return `${fmt(days)}d ${fmt(hours)}:${fmt(minutes)}:${fmt(seconds)}`;
  }
  return `${fmt(hours)}:${fmt(minutes)}:${fmt(seconds)}`;
}

export function VotacaoEtapa3() {
  const { user } = useAuth();
  const { showModal, closeModal } = useModal();
  const periodo = usePeriodoFase(PHASE_IDS.ETAPA_3_PREMIO);
  const location = useLocation();

  const [categoriaSelecionada, setCategoriaSelecionada] = useState<string>(() => {
    const fromState = (location.state as { categoria?: string } | null)?.categoria;
    const valido = CATEGORIAS_PARTICIPANTES.some((c) => c.id === fromState);
    return valido ? fromState! : CATEGORIAS_PARTICIPANTES[0].id;
  });
  const [busca, setBusca] = useState('');
  const [debouncedBusca, setDebouncedBusca] = useState('');
  const [todasIniciativas, setTodasIniciativas] = useState<IniciativaFormatada[]>([]);
  const [loadingIniciativas, setLoadingIniciativas] = useState(true);
  const [erroIniciativas, setErroIniciativas] = useState<string | null>(null);
  const [isExportingPdf, setIsExportingPdf] = useState(false);
  const [isCapturingConvite, setIsCapturingConvite] = useState(false);
  const [floatingDismissed, setFloatingDismissed] = useState<Record<string, boolean>>({});
  const [tutorialEtapa3, setTutorialEtapa3] = useState<Tutorial | null>(null);

  const {
    votos,
    limite,
    perfil,
    vpDestino,
    isReadOnly,
    statusPreliminar,
    preliminarSuperseded,
    preliminarVp,
    mostrarModalPreliminar,
    isLoadingRascunho,
    isSaving,
    isSubmitting,
    submitError,
    handleToggleVoto,
    handleEnviarVotos,
    handleEnviarParaAprovacao,
    aceitarPreliminar,
    recusarPreliminar,
    prontoParaEnvio,
    podeEnviar,
  } = useVotacaoEtapa3();

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const shareCardRef = useRef<HTMLElement>(null);

  const countdown = useCountdown(periodo?.fim ?? null);
  const startLabel = periodo ? formatarDiaMes(periodo.inicio) : '';
  const endLabel = periodo ? formatarDiaMes(periodo.fim) : '';

  const progresso = useMemo(() => {
    if (!periodo) return 0;
    const total = periodo.fim.getTime() - periodo.inicio.getTime();
    const decorrido = Date.now() - periodo.inicio.getTime();
    if (total <= 0) return 0;
    return Math.min(Math.max((decorrido / total) * 100, 0), 100);
  }, [periodo]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedBusca(busca.trim()), 350);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [busca]);

  useEffect(() => {
    setBusca('');
    setDebouncedBusca('');
  }, [categoriaSelecionada]);

  useEffect(() => {
    let canceled = false;
    if (!user?._id) return;
    getTutorial(getTutorialEtapa3IdForUser(user))
      .then((t) => { if (!canceled) setTutorialEtapa3(t ?? null); })
      .catch(() => { if (!canceled) setTutorialEtapa3(null); });
    return () => { canceled = true; };
  }, [user?._id]);

  useEffect(() => {
    let cancelled = false;
    async function carregar() {
      try {
        setLoadingIniciativas(true);
        setErroIniciativas(null);
        const items = await buscarIniciativasEtapa3(user?._id);
        if (cancelled) return;
        setTodasIniciativas(items);
      } catch {
        if (cancelled) return;
        setErroIniciativas('Não foi possível carregar as iniciativas. Tente novamente.');
        setTodasIniciativas([]);
      } finally {
        if (!cancelled) setLoadingIniciativas(false);
      }
    }
    carregar();
    return () => { cancelled = true; };
  }, [user?._id]);

  // Abre o modal de escolha do VP. O hook só seta mostrarModalPreliminar=true uma
  // vez (no mount); aceitarPreliminar/recusarPreliminar o voltam para false e o
  // guard abaixo evita reabertura. Não há closeModal no cleanup de propósito.
  useEffect(() => {
    if (!mostrarModalPreliminar) return;
    showModal(
      'preliminar-etapa3-escolha',
      <ModalEscolhaPreliminarEtapa3
        assessorNome={preliminarVp?.assessorNome}
        onManterVoto={() => {
          closeModal();
          recusarPreliminar();
        }}
        onCarregarVoto={() => {
          closeModal();
          aceitarPreliminar();
        }}
      />,
      'preliminar-etapa3-escolha-wrapper'
    );
  }, [
    mostrarModalPreliminar,
    showModal,
    closeModal,
    aceitarPreliminar,
    recusarPreliminar,
    preliminarVp,
  ]);

  const votosCategoriaAtual = votos[categoriaSelecionada]?.ids ?? [];
  const atingiuLimite = votosCategoriaAtual.length >= limite;

  const contadores = useMemo(() => {
    const map: Record<string, number> = {};
    CATEGORIAS_PARTICIPANTES.forEach((cat) => {
      map[cat.id] = votos[cat.id]?.ids?.length || 0;
    });
    return map;
  }, [votos]);

  const iniciativas = useMemo(() => {
    const termo = debouncedBusca.toLowerCase();
    return todasIniciativas.filter(
      (i) =>
        i.categoryPremio === categoriaSelecionada &&
        (termo.length === 0 || i.titulo.toLowerCase().includes(termo))
    );
  }, [todasIniciativas, categoriaSelecionada, debouncedBusca]);

  const categoriasPendentes = useMemo(
    () => limite === 1
      ? CATEGORIAS_PARTICIPANTES.filter((cat) => (contadores[cat.id] ?? 0) < 1)
      : [],
    [limite, contadores]
  );

  const podeClicarEnviar = limite === 1 ? podeEnviar : prontoParaEnvio;

  const categoriaInfo = useMemo(
    () => CATEGORIAS_PARTICIPANTES.find((c) => c.id === categoriaSelecionada),
    [categoriaSelecionada]
  );

  const handleClickEnviar = useCallback(() => {
    if (limite === 1 && categoriasPendentes.length > 0) {
      showModal(
        'confirmar-envio-etapa3',
        <ModalWarning
          title="Categorias sem voto"
          description={
            <div className="py-3">
              <p className="mb-3">Você ainda não votou em todas as categorias. Deseja enviar mesmo assim?</p>
              <p className="font-semibold mb-1">Pendentes:</p>
              <ul className="list-disc pl-5 space-y-1">
                {categoriasPendentes.map((cat) => (
                  <li key={cat.id}>{cat.nome}</li>
                ))}
              </ul>
            </div>
          }
          btnText="Enviar mesmo assim"
          hasBackButton
          backBtnText="Voltar e votar"
          onBackButton={closeModal}
          onClose={() => {
            closeModal();
            handleEnviarVotos();
          }}
        />
      );
      return;
    }
    handleEnviarVotos();
  }, [limite, categoriasPendentes, showModal, closeModal, handleEnviarVotos]);

  const handleProximaCategoria = useCallback(() => {
    const indiceAtual = CATEGORIAS_PARTICIPANTES.findIndex(
      (categoria) => categoria.id === categoriaSelecionada
    );
    if (indiceAtual === -1) return;
    const total = CATEGORIAS_PARTICIPANTES.length;
    // Procura a próxima ainda não completa; se não houver, avança ciclicamente.
    for (let offset = 1; offset <= total; offset++) {
      const proxima = CATEGORIAS_PARTICIPANTES[(indiceAtual + offset) % total];
      const completa = (votos[proxima.id]?.ids?.length || 0) >= limite;
      if (!completa) {
        setCategoriaSelecionada(proxima.id);
        return;
      }
    }
    const proxima = CATEGORIAS_PARTICIPANTES[(indiceAtual + 1) % total];
    setCategoriaSelecionada(proxima.id);
  }, [categoriaSelecionada, votos, limite]);

  const handleBaixarConvite = useCallback(async () => {
    if (isCapturingConvite || !shareCardRef.current) return;
    setIsCapturingConvite(true);
    // Dois RAFs garantem que o React aplicou o swap CTA → mensagem antes da
    // captura, evitando que o botão original apareça na imagem baixada.
    await new Promise((resolve) => requestAnimationFrame(() => resolve(null)));
    await new Promise((resolve) => requestAnimationFrame(() => resolve(null)));

    try {
      const blob = await toBlob(shareCardRef.current, { pixelRatio: 2 });
      if (!blob) return;
      const file = new File([blob], 'premio-bb-convite.png', { type: 'image/png' });
      const downloadUrl = URL.createObjectURL(file);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = file.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('[VotacaoEtapa3] erro ao gerar convite', error);
    } finally {
      setIsCapturingConvite(false);
    }
  }, [isCapturingConvite]);

  const handleExportarPdf = useCallback(async () => {
    try {
      setIsExportingPdf(true);
      const itensParaExportar: VotacaoEtapa1ExportItem[] = todasIniciativas.map(
        (iniciativa) => ({
          titulo: iniciativa.titulo,
          descricao: iniciativa.descricao || '',
          resultado: iniciativa.resultado || '',
          videoUrl: iniciativa.originalVideoUrl || iniciativa.videoUrl,
          categoria: iniciativa.categoria,
        })
      );
      if (itensParaExportar.length > 0) {
        exportarIniciativasPdf(itensParaExportar, 'iniciativas-etapa3-todas-categorias');
      }
    } catch (error) {
      console.error('Erro ao exportar PDF:', error);
    } finally {
      setIsExportingPdf(false);
    }
  }, [todasIniciativas]);

  const abrirTutorialEtapa3 = () => {
    if (!tutorialEtapa3?.steps?.length) return;
    showModal(
      'votacao-etapa3-tutorial-modal',
      <VotacaoEtapa3TutorialModal
        steps={tutorialEtapa3.steps}
        onClose={closeModal}
        onStartVoting={closeModal}
      />,
      'votacao-etapa3-tutorial-wrapper',
      false
    );
  };

  return (
    <div id="votacao-etapa-3-wrapper">
      <Header currentPage="inicio" />

      <section className="votacao-etapa-3-hero">
        <div className="votacao-etapa-3-hero__content">
          <h1 className="votacao-etapa-3-hero__title">Etapa 3 de avaliação</h1>
          <p className="votacao-etapa-3-hero__description">
            {limite === 1 ? "Vote nas iniciativas que mais representam as categorias do Prêmio BB, selecionando 1 iniciativa de cada categoria." : "Vote nas iniciativas que mais representam as categorias do Prêmio BB, selecionando 3 iniciativa de cada categoria."} {" "}
            <button
              type="button"
              className="votacao-etapa-3-hero__link"
              onClick={abrirTutorialEtapa3}
            >
              Saiba aqui como votar!
            </button>
          </p>

          {perfil === 'assessor' && vpDestino && (
            <p className="votacao-etapa-3-hero__assessor-banner">
              {preliminarSuperseded ? (
                <>O voto foi enviado por <strong>{vpDestino.nome}</strong>.</>
              ) : statusPreliminar === 'em_aprovacao' ? (
                <>Voto preliminar enviado para <strong>{vpDestino.nome}</strong> — aguardando a revisão.</>
              ) : (
                <>Você está preparando o voto preliminar para <strong>{vpDestino.nome}</strong>.</>
              )}
            </p>
          )}

          {perfil === 'vp' && preliminarVp && !mostrarModalPreliminar && (
            <p className="votacao-etapa-3-hero__assessor-banner">
              Você aceitou o voto preliminar preparado por{' '}
              <strong>{preliminarVp.assessorNome}</strong>. Revise e envie quando estiver pronto.
            </p>
          )}

          {periodo && (
            <>
              <div className="votacao-etapa-3-hero__timer">
                <span className="votacao-etapa-3-hero__timer-label">
                  O PERÍODO DE VOTAÇÃO SERÁ FINALIZADA EM:
                </span>
                <span className="votacao-etapa-3-hero__timer-badge">
                  <IoTimeOutline aria-hidden="true" />
                  {countdown}
                </span>
              </div>

              <div className="votacao-etapa-3-hero__progress">
                <span className="votacao-etapa-3-hero__progress-date">De {startLabel}</span>
                <div className="votacao-etapa-3-hero__progress-bar">
                  <div
                    className="votacao-etapa-3-hero__progress-fill"
                    style={{ width: `${progresso}%` }}
                  />
                </div>
                <span className="votacao-etapa-3-hero__progress-date">Até {endLabel}</span>
              </div>
            </>
          )}
        </div>
      </section>

      <main className="votacao-etapa-3-main">
        <CategoriasParticipantes
          title="Selecione a categoria:"
          categoriaSelecionada={categoriaSelecionada}
          onCategoriaChange={setCategoriaSelecionada}
          contadores={contadores}
          limitePorCategoria={limite}
        />

        <div className="votacao-etapa-3-footer-actions votacao-etapa-3-footer-actions--top">
          <button
            type="button"
            className="votacao-etapa-3-export"
            onClick={handleExportarPdf}
            disabled={isExportingPdf || todasIniciativas.length === 0}
          >
            <IoCloudDownloadOutline size={18} aria-hidden="true" />
            {isExportingPdf ? 'GERANDO PDF...' : 'EXPORTAR PDF'}
          </button>

          {perfil === 'assessor'
            ? !isReadOnly && (
                <button
                  type="button"
                  className="votacao-etapa-3-footer-actions__send"
                  onClick={handleEnviarParaAprovacao}
                  disabled={!prontoParaEnvio || isSubmitting}
                  aria-disabled={!prontoParaEnvio || isSubmitting}
                  title={
                    prontoParaEnvio
                      ? 'Enviar voto preliminar para aprovação do VP'
                      : `Vote em ${limite} iniciativas em cada categoria para habilitar o envio.`
                  }
                >
                  {isSubmitting ? 'ENVIANDO...' : 'ENVIAR PARA APROVAÇÃO'}
                </button>
              )
            : podeEnviar && (
                <button
                  type="button"
                  className="votacao-etapa-3-footer-actions__send"
                  onClick={handleClickEnviar}
                  disabled={!podeClicarEnviar}
                  aria-disabled={!podeClicarEnviar}
                  title={
                    podeClicarEnviar
                      ? 'Enviar votos da Etapa 3'
                      : `Vote em ${limite} iniciativa${limite > 1 ? 's' : ''} em cada categoria para habilitar o envio.`
                  }
                >
                  ENVIAR VOTOS
                </button>
              )}
        </div>

        {submitError && (
          <p className="votacao-etapa-3-submit-error" role="alert">
            {submitError}
          </p>
        )}

        <section className="votacao-etapa-3-resumo" aria-live="polite">
          <ul className="votacao-etapa-3-resumo__list">
            {CATEGORIAS_PARTICIPANTES.map((categoria) => {
              const count = contadores[categoria.id] || 0;
              const completo = count >= limite;
              return (
                <li
                  key={categoria.id}
                  className={`votacao-etapa-3-resumo__item${completo ? ' is-completed' : ''}`}
                >
                  <strong>{categoria.nome}</strong>
                  <span> - ({count}/{limite})</span>
                </li>
              );
            })}
          </ul>
        </section>

        <section className="votacao-etapa-3-current">
          <div className="votacao-etapa-3-current__card">
            {categoriaInfo && (
              <img
                src={categoriaInfo.imagem}
                alt=""
                aria-hidden="true"
                className="votacao-etapa-3-current__icon"
              />
            )}
            <div className="votacao-etapa-3-current__label">
              <strong>{categoriaInfo?.nome}</strong>
              <span> ({votosCategoriaAtual.length}/{limite} selecionadas)</span>
            </div>
          </div>

          <button
            type="button"
            className="votacao-etapa-3-current__next"
            onClick={handleProximaCategoria}
          >
            PRÓXIMA CATEGORIA
          </button>
        </section>

        <div className="votacao-etapa-3-filters-row">
          <div className="votacao-etapa-3-search">
            <input
              type="search"
              value={busca}
              onChange={(event) => setBusca(event.target.value)}
              placeholder="O que você procura?"
              className="votacao-etapa-3-search__input"
              aria-label="Buscar iniciativa"
            />
            <IoSearch className="votacao-etapa-3-search__icon" aria-hidden="true" />
          </div>
        </div>

        <section className="votacao-etapa-3-list" aria-live="polite">
          {loadingIniciativas && (
            <p className="votacao-etapa-3-list__empty">Carregando iniciativas...</p>
          )}

          {!loadingIniciativas && erroIniciativas && (
            <p className="votacao-etapa-3-list__empty" role="alert">{erroIniciativas}</p>
          )}

          {!loadingIniciativas && !erroIniciativas && iniciativas.length === 0 && (
            <p className="votacao-etapa-3-list__empty">
              Nenhuma iniciativa encontrada para esta categoria.
            </p>
          )}

          {!loadingIniciativas && !erroIniciativas && iniciativas.length > 0 && (
            <div className="votacao-etapa-3-list__grid">
              {iniciativas.map((iniciativa) => {
                const votada = votosCategoriaAtual.includes(iniciativa.id);
                return (
                  <IniciativaCard
                    key={iniciativa.id}
                    {...iniciativa}
                    votar={{
                      votada,
                      onVotar: () => handleToggleVoto(iniciativa.id, iniciativa.titulo, categoriaSelecionada),
                      disabled: isReadOnly || (atingiuLimite && !votada),
                      disabledLabel: isReadOnly
                        ? 'VOTO ENVIADO'
                        : `LIMITE ATINGIDO (${limite}/${limite})`,
                    }}
                  />
                );
              })}

              <article
                ref={shareCardRef}
                className="votacao-etapa-3-share-card"
                aria-label="Compartilhe o Prêmio BB"
                key="share-card"
              >
                <div className="votacao-etapa-3-share-card__top">
                  <img
                    src={trofeuBannerCompartilhamento}
                    alt=""
                    aria-hidden="true"
                    className="votacao-etapa-3-share-card__trofeu"
                  />
                  <h3 className="votacao-etapa-3-share-card__title">
                    Eu já escolhi as iniciativas do{' '}
                    <span className="votacao-etapa-3-share-card__title--highlight">Prêmio BB</span>{' '}
                    que me inspiram.
                  </h3>
                </div>
                <div className="votacao-etapa-3-share-card__subtitle-container">
                  <p className="votacao-etapa-3-share-card__subtitle">E você, já votou?</p>
                </div>
                {isCapturingConvite ? (
                  <div
                    className="votacao-etapa-3-share-card__cta votacao-etapa-3-share-card__cta--mensagem"
                    aria-hidden="true"
                  >
                    <span className="votacao-etapa-3-share-card__cta-text">
                      Olá, venha votar no Prêmio BB comigo! <br></br>Acesse
                      {' '}
                      <span className="votacao-etapa-3-share-card__cta-highlight">
                        http://premiobb.com.br/
                      </span>
                    </span>
                  </div>
                ) : (
                  <button
                    type="button"
                    className="votacao-etapa-3-share-card__cta"
                    onClick={handleBaixarConvite}
                    disabled={isCapturingConvite}
                  >
                    <IoPaperPlane size={24} className="votacao-etapa-3-share-card__cta-icon" aria-hidden="true" />
                    <span className="votacao-etapa-3-share-card__cta-text">
                      Baixe o convite e <span className="votacao-etapa-3-share-card__cta-highlight">compartilhe com mais pessoas </span>
                      para votar! #premiobb
                    </span>
                  </button>
                )}
              </article>
            </div>
          )}
        </section>
      </main>

      {!isReadOnly && ((prontoParaEnvio && limite === 3) ||
        (!floatingDismissed[categoriaSelecionada] && categoriaInfo)) && categoriaInfo && (
          <aside
            className="votacao-etapa-3-floating-card"
            role="status"
            aria-live="polite"
          >
            {prontoParaEnvio && limite === 3 ? (
              <>
                <h4>Parabéns! Você completou todas as categorias.</h4>
                <p>
                  Agora é só enviar seus votos para concluir a Etapa 3. Você ainda
                  pode revisar antes de enviar.
                </p>
                <div className="votacao-etapa-3-floating-card__actions">
                  <button
                    type="button"
                    className="votacao-etapa-3-floating-card__btn"
                    onClick={perfil === 'assessor' ? handleEnviarParaAprovacao : handleClickEnviar}
                    disabled={isSubmitting}
                  >
                    {isSubmitting
                      ? 'ENVIANDO...'
                      : perfil === 'assessor'
                      ? 'ENVIAR PARA APROVAÇÃO'
                      : 'ENVIAR VOTOS'}
                  </button>
                </div>
              </>
            ) : atingiuLimite ? (
              <>
                <h4>
                  Parabéns! Você votou em {votosCategoriaAtual.length}{' '}
                  iniciativa{votosCategoriaAtual.length === 1 ? '' : 's'}{' '}
                  inspiradora{votosCategoriaAtual.length === 1 ? '' : 's'} da categoria{' '}
                  {categoriaInfo.nome}!
                </h4>
                <p>
                  Caso queira votar em outra iniciativa dessa categoria, será necessário
                  desmarcar a votada anteriormente.
                </p>
                <p className="votacao-etapa-3-floating-card__cta">
                  {limite === 1
                    ? 'Seu voto já está registrado. Você pode editar a qualquer momento.'
                    : 'Que tal votar nas outras categorias antes de enviar seu voto?'}
                </p>
                <div className="votacao-etapa-3-floating-card__actions">
                  <button
                    type="button"
                    className="votacao-etapa-3-floating-card__btn votacao-etapa-3-floating-card__btn--ghost"
                    onClick={() => {
                      setFloatingDismissed((prev) => ({
                        ...prev,
                        [categoriaSelecionada]: true,
                      }));
                      handleProximaCategoria();
                    }}
                  >
                    PRÓXIMA CATEGORIA
                  </button>
                  {limite === 1 && (
                    <button
                      type="button"
                      className="votacao-etapa-3-floating-card__btn"
                      onClick={handleClickEnviar}
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'ENVIANDO...' : 'ENVIAR VOTO'}
                    </button>
                  )}
                </div>
              </>
            ) : (
              (() => {
                const restantes = limite - votosCategoriaAtual.length;
                const plural = restantes === 1 ? '' : 's';
                return (
                  <>
                    <h4>
                      Você ainda não votou em {restantes} iniciativa{plural}{' '}
                      inspiradora{plural} da categoria {categoriaInfo.nome}!
                    </h4>
                    <p>
                      É necessário votar em{' '}
                      <span className="votacao-etapa-3-floating-card__highlight">
                        {restantes} iniciativa{plural}
                      </span>{' '}
                      para salvar o seu voto nessa categoria.
                    </p>
                  </>
                );
              })()
            )}
          </aside>
        )}
    </div>
  );
}
